import { SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';

import { FAIL_REPORT_URLS } from '@/constants/urls';
import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { RESOURCES } from '@/Resources';

export const useFailReportFilter = (formProps: FormProps, filters: any[]) => {
  const { form } = formProps;
  const initialValues: Record<string, any> = useMemo(
    () =>
      Object.fromEntries(
        filters.map(filter => {
          const valueParsed = parseInt(filter.value, 10);
          const value = isNaN(valueParsed) ? filter.value : valueParsed;

          return [filter.field, value];
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useFormInitialSet(form, initialValues, [
    {
      watchField: 'category',
      resetFields: ['subcategory'],
    },
  ]);

  const { ticketsSelectProps, categorySelectProps } = useOutletContext<{
    ticketsSelectProps: SelectProps;
    categorySelectProps: SelectProps;
  }>();
  // El selector de subcategorías depende de la categoría seleccionada, por lo que se le pasan los valores de filtro correspondientes
  const categoryId = formProps?.form?.getFieldValue('category');
  const { selectProps: subcategorySelectProps } = useSelectScrollSearch({
    resource: FAIL_REPORT_URLS.FAIL_SUB_CATEGORIES,
    optionLabel: item => item.title,
    optionValue: item => item.id,
    filters: categoryId ? [{ field: 'category', operator: 'eq', value: categoryId }] : [],
    queryOptions: {
      enabled: Boolean(categoryId), // Solo habilita la consulta si hay una categoría seleccionada
    },
  });
  // Datos iniciales
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: initialValues?.ticket,
    labelKey: (item: any) =>
      `${item.id} - ${item.title ? item.title : item.ticket_title_str ? item.ticket_title_str : '-'}`,
    valueKey: 'id',
  });
  const categorySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: categorySelectProps,
    resource: FAIL_REPORT_URLS.FAIL_CATEGORIES,
    initialValue: initialValues?.category,
    labelKey: 'title',
    valueKey: 'id',
  });
  const subcategorySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: subcategorySelectProps,
    resource: FAIL_REPORT_URLS.FAIL_SUB_CATEGORIES,
    initialValue: initialValues?.subcategory,
    labelKey: 'title',
    valueKey: 'id',
  });
  const { selectProps: usersSelectProps } = useSelectScrollSearch({
    resource: 'users',
    optionLabel: (item: any) => `${item.first_name} ${item.last_name} (${item.username})`,
    pagination: {
      mode: 'off',
      pageSize: 3000,
    },
    meta: {
      empresa: 4,
    },
  });
  const usersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: usersSelectProps,
    resource: 'users',
    initialValue: initialValues.created_by,
    labelKey: (item: any) => `${item.first_name} ${item.last_name} (${item.username})`,
    valueKey: 'id',
  });

  return {
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    categorySelectProps: categorySelectPropsWithInitials,
    subcategorySelectProps: subcategorySelectPropsWithInitials,
    usersSelectProps: usersSelectPropsWithInitials,
  };
};
